import React, { useEffect, useRef, useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
} from "@mui/material";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import { XYZ } from "ol/source";
import { fromLonLat, toLonLat } from "ol/proj";
import { Point } from "ol/geom";
import Feature from "ol/Feature";
import { Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import { Style, Icon } from "ol/style";
import { defaults as defaultControls } from "ol/control";
import "ol/ol.css";

function MapModal({ open, onClose, initialLat, initialLon, onSave }) {
	const mapRef = useRef(null);
	const [coordinates, setCoordinates] = useState({
		latitude: initialLat || -3.71839,
		longitude: initialLon || -38.5434,
	});

	useEffect(() => {
		if (!open) return;

		// Definir coordenadas iniciais
		const startLat = initialLat || -3.71839;
		const startLon = initialLon || -38.5434;

		setCoordinates({
			latitude: startLat,
			longitude: startLon,
		});

		// Aguardar o DOM estar pronto
		const timer = setTimeout(() => {
			if (!mapRef.current) return;

			// Configurar fonte de tiles do Mapbox
			const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN || "pk.eyJ1Ijoicm9idHAiLCJhIjoiY21hdmdoMjNpMDRoeTJub3I3aGZiYndrMyJ9.mLo63CfWf7NqzeXAA9flZw";
			const mapboxSource = new XYZ({
				url: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}@2x?access_token=${mapboxToken}`,
				tilePixelRatio: 2,
				maxZoom: 19,
			});

			// Criar camada de vetor para o marcador
			const markerSource = new VectorSource();
			const markerVectorLayer = new VectorLayer({
				source: markerSource,
			});

			// Converter coordenadas iniciais para projeção do mapa
			const initialCenter = fromLonLat([startLon, startLat]);

			// Criar o mapa
			const initialMap = new Map({
				target: mapRef.current,
				controls: defaultControls(),
				layers: [
					new TileLayer({
						source: mapboxSource,
					}),
					markerVectorLayer,
				],
				view: new View({
					center: initialCenter,
					zoom: 16,
				}),
			});

			// Adicionar marcador inicial
			addMarker(markerSource, initialCenter);

			// Adicionar evento de clique no mapa
			initialMap.on("click", (event) => {
				const clickedCoord = event.coordinate;
				const [lon, lat] = toLonLat(clickedCoord);

				setCoordinates({
					latitude: lat,
					longitude: lon,
				});

				// Limpar marcadores anteriores e adicionar novo
				markerSource.clear();
				addMarker(markerSource, clickedCoord);
			});

			// Forçar atualização do tamanho do mapa
			setTimeout(() => {
				initialMap.updateSize();
			}, 100);
		}, 100);

		// Cleanup
		return () => {
			clearTimeout(timer);
		};
	}, [open, initialLat, initialLon]);

	const addMarker = (source, coordinate) => {
		const marker = new Feature({
			geometry: new Point(coordinate),
		});

		marker.setStyle(
			new Style({
				image: new Icon({
					src: "data:image/svg+xml;charset=utf-8," + encodeURIComponent(`
						<svg xmlns="http://www.w3.org/2000/svg" width="32" height="48" viewBox="0 0 32 48">
							<path fill="%2317A2B8" d="M16 0C7.2 0 0 7.2 0 16c0 12 16 32 16 32s16-20 16-32c0-8.8-7.2-16-16-16zm0 22c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6z"/>
						</svg>
					`),
					scale: 1,
					anchor: [0.5, 1],
				}),
			})
		);

		source.addFeature(marker);
	};

	const handleSave = () => {
		onSave(coordinates.latitude, coordinates.longitude);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: {
					height: "80vh",
					maxHeight: "700px",
				},
			}}
		>
			<DialogTitle sx={{ bgcolor: "#1a9e8f", color: "white" }}>
				Mova o alfinete para o ponto desejado
			</DialogTitle>
			<DialogContent sx={{ p: 0, position: "relative", height: "100%", display: "flex", flexDirection: "column" }}>
				<Box
					ref={mapRef}
					sx={{
						width: "100%",
						height: "500px",
						minHeight: "500px",
						flex: 1,
					}}
				/>
				<Box
					sx={{
						position: "absolute",
						bottom: 16,
						left: "50%",
						transform: "translateX(-50%)",
						bgcolor: "white",
						px: 2,
						py: 1,
						borderRadius: 1,
						boxShadow: 3,
					}}
				>
					<Typography variant="body2">
						Lat: {coordinates.latitude.toFixed(6)}, Lon: {coordinates.longitude.toFixed(6)}
					</Typography>
				</Box>
			</DialogContent>
			<DialogActions sx={{ p: 2 }}>
				<Button onClick={onClose} sx={{ color: "#666" }}>
					Cancelar
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					sx={{
						bgcolor: "#1a9e8f",
						"&:hover": {
							bgcolor: "#138a7d",
						},
					}}
				>
					Registrar coordenadas
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default MapModal;
